/** Where the game server lives.
 *  - Web (midomino.io): '' → relative URLs, same origin (unchanged behavior).
 *  - iOS/Android app (bundled www, origin capacitor://localhost): absolute server URL. */
const NATIVE = !!((window as any).Capacitor && (window as any).Capacitor.isNativePlatform && (window as any).Capacitor.isNativePlatform());
export const SERVER_URL = 'https://www.midomino.io';
export const API_BASE: string = NATIVE ? SERVER_URL : '';

/** iOS/Android app: lay the table out at the desktop design width when the phone
 *  is held sideways, so the app looks exactly like midomino.io (the web view
 *  scales it down to fit the screen). Portrait keeps the phone layout. */
if (NATIVE) {
  // The phone's emoji font is missing on some devices/simulators, so the emoji
  // icons (hands, avatars, tabs) showed "?". Ship a colour emoji font with the
  // app and use it as the last fallback for both UI fonts.
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'styles/emoji/emoji.css';
  document.head.appendChild(link);
  const st = document.createElement('style');
  st.textContent = ":root{--font-display:'Montserrat','Segoe UI','Apple Color Emoji','Noto Emoji',system-ui,sans-serif;--font-body:'Nunito','Segoe UI','Apple Color Emoji','Noto Emoji',system-ui,sans-serif;}";
  document.head.appendChild(st);
  // Flags (regional-indicator pairs like 🇵🇷) can't be drawn by the phone's emoji
  // font, so the app shows the two-letter country code instead. Native only.
  const RI = /[\u{1F1E6}-\u{1F1FF}]{2}/gu;
  const fixFlags = (root: Node) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    while (walker.nextNode()) nodes.push(walker.currentNode as Text);
    for (const n of nodes) {
      const v = n.nodeValue || '';
      if (!RI.test(v)) { RI.lastIndex = 0; continue; }
      RI.lastIndex = 0;
      n.nodeValue = v.replace(RI, (f) => String.fromCharCode(f.codePointAt(0)! - 0x1F1E6 + 65, f.codePointAt(1)! - 0x1F1E6 + 65));
    }
  };
  const startFlags = () => {
    fixFlags(document.body);
    let queued = false;   // batch DOM bursts into one pass per frame (keeps the table smooth)
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fixFlags(document.body); });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startFlags);
  else startFlags();
  const fit = () => {
    const m = document.querySelector('meta[name="viewport"]');
    if (!m) return;
    const land = window.innerWidth > window.innerHeight;
    document.documentElement.classList.toggle('native-land', land);
    m.setAttribute('content', land
      ? 'width=1600, viewport-fit=cover, user-scalable=no'
      : 'width=device-width, viewport-fit=cover, maximum-scale=1, user-scalable=no');
  };
  fit();
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
}

/* Phone browsers (midomino.io on a phone): same landscape table as the app, so every
 *  player sees the same layout. Desktop and tablets are untouched. */
if (!NATIVE && window.matchMedia && window.matchMedia('(pointer: coarse)').matches && Math.min(window.innerWidth, window.innerHeight) <= 500) {
  const fitWeb = () => {
    const m = document.querySelector('meta[name="viewport"]');
    if (!m) return;
    const land = window.innerWidth > window.innerHeight;
    document.documentElement.classList.toggle('native-land', land);
    m.setAttribute('content', land
      ? 'width=1600, viewport-fit=cover, user-scalable=no'
      : 'width=device-width, viewport-fit=cover, maximum-scale=1, user-scalable=no');
  };
  fitWeb();
  window.addEventListener('resize', fitWeb);
  window.addEventListener('orientationchange', fitWeb);
}
